import React, { useEffect, useState } from "react";
import phone from "../assets/icons/phone.svg";
import emails from "../assets/icons/email.svg";
import location from "../assets/icons/location.svg";
import curve from "../assets/icons/curvyline.svg";
import dot from "../assets/img/contact-dot.png";
import MakeDifference from "./Reusable/MakeDifference";
import SEO from "./SEO";
import ReactGA from "react-ga4";

const Contact = () => {
  // Add state for form data and validation
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters long";
    }

    // Email validation
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters long";
    }

    return newErrors;
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(
        "https://office.synergycarefoundation.org/api/contact",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      if (response.ok) {
        setSubmitMessage(
          "Thank you for your message. We'll get back to you soon!"
        );
        ReactGA.event({
          hitType: "leadconversion",
          action: "Form Submitted",
          title: "Contact Form Submission",
        });
        setFormData({ name: "", email: "", message: "" });
      } else {
        setSubmitMessage("Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitMessage("Failed to send message. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: "/contact",
      title: "Contact SynergyCare Foundation",
    });
  });

  return (
    <section className="relative">
      <SEO
        title="Contact SynergyCare Foundation"
        description="Get in touch with SynergyCare Foundation for support, partnerships, or volunteer opportunities."
        name="SynergyCare Foundation"
        type=""
      />
      <div className="relative max-w-[1440px] w-[90%] mx-auto lg:pt-40 pt-24 pb-16 space-y-8">
        <h1 className="lg:text-[64px] text-5xl lg:leading-[76px] leading-[60px] font-semibold text-[#11112D] mb-4 text-center">
          Contact Us
        </h1>
        <p className="text-lg text-[#11112d] font-normal mb-8 text-center w-full lg:w-[60%] mx-auto hidden lg:block">
          Are you a patient in need of support, a healthcare provider seeking a
          partnership, or a supporter ready to contribute? Our doors are always
          open to you.
        </p>

        <div className="flex lg:flex-row flex-col-reverse items-center justify-center xl:gap-[102px] gap-20 pb-40	">
          <form
            onSubmit={handleSubmit}
            className="space-y-8 lg:max-w-[542px] w-full"
          >
            <p className="text-lg text-[#11112d] font-normal mb-8 text-center w-full lg:w-[60%] mx-auto block lg:hidden">
              Are you a patient in need of support, a healthcare provider
              seeking a partnership, or a supporter ready to contribute? Our
              doors are always open to you.
            </p>
            <div className="relative">
              <input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Name*"
                className={`w-full border-t-0 border-l-0 border-r-0 border-b-2 outline-none ${
                  errors.name ? "border-red-500" : ""
                }`}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
            </div>

            <div className="relative">
              <input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Email*"
                className={`w-full border-t-0 border-l-0 border-r-0 border-b-2 outline-none ${
                  errors.email ? "border-red-500" : ""
                }`}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>

            <div className="relative">
              <input
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Leave us a message*"
                className={`w-full border-t-0 border-l-0 border-r-0 border-b-2 outline-none ${
                  errors.message ? "border-red-500" : ""
                }`}
              />
              {errors.message && (
                <p className="text-red-500 text-sm mt-1">{errors.message}</p>
              )}
            </div>

            {submitMessage && (
              <p
                className={`text-sm ${
                  submitMessage.includes("error")
                    ? "text-red-500"
                    : "text-green-500"
                }`}
              >
                {submitMessage}
              </p>
            )}
            <p className="text-[#45E99E] font-semibold">
              By submitting this form, I agree to volunteer in accordance with
              the guidelines of SynergyCare Foundation
            </p>
            <button
              type="submit"
              disabled={isSubmitting}
              className="text-white text-xs/4 md:text-base/6 bg-[#45E99E] w-full md:py-5 py-[14px] rounded-[43px] md:rounded-[68px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Sending..." : "Send message"}
            </button>
          </form>
          <div className="space-y-6 lg:max-w-[501px] w-full">
            <div className="flex gap-7">
              <img src={location} alt="Icon 4" className="w-7 h-7" />
              <p className="flex flex-col space-y-[14px]">
                <span>
                  <b>Maryland Office:</b> 403 West Pennsylvania Avenue, Towson,
                  Maryland 21204
                </span>
                <span>
                  <b>Washington DC Office:</b> 1818 New York Avenue, Suite 206.
                  North East Washington DC 20002
                </span>
              </p>
            </div>
            <div className="flex gap-7 ">
              <img src={phone} alt="Icon 4" className="w-7 h-7" />
              <p>
                <b>Washington DC Branch</b>: (202) 909-1109
                <br /> <b>Washington DC Toll-free Line</b>: (443) 491-9380
                <br /> <b>Maryland Branch</b>: (443) 491-9382
                <br /> <b>Toll-free</b>: (888) 416-6573
              </p>
            </div>

            <div className="flex gap-7 w-[300px]">
              <img src={emails} alt="Icon 3" className="w-7 h-7" />
              <p>info@synergycarefoundation.org</p>
            </div>
          </div>
        </div>

        <img src={dot} alt="Icon 2" className="absolute  left-0 bottom-10" />
        <img
          src={curve}
          alt="Icon 1"
          className="absolute md:flex hidden top-4 lg:top-16 lg:right-20 right-0"
        />
      </div>
      <MakeDifference
        title1={"Ready To make a"}
        title2={" Difference? "}
        description={
          "Join our team of volunteers who are making a direct impact on under-served communities. Your time can change lives!"
        }
      />
    </section>
  );
};

export default Contact;
